import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box, Select, Table, Thead, Tbody, Tr, Th, Td, Button, useToast, Flex, Text, Input,
  Menu, MenuButton, MenuList, MenuItem, IconButton,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  FormControl, FormLabel, Textarea, Tooltip,
} from "@chakra-ui/react";
import { FaPlus, FaEllipsisV } from "react-icons/fa";
import PageToolbar from "../../../components/shared/PageToolbar";
import { Panel } from "../../../components/shared/Section";
import { TableSkeleton } from "../../../components/shared/LoadingBlock";
import EditablePopover from "./components/EditablePopover";
import LogoCell from "./components/LogoCell";
import { PagesPopover, RetailersPopover, SuppliersPopover, BillingPopover } from "./components/GrantPopovers";
import OrganisationsView, { CreateOrgPopover } from "./OrganisationsView";
import { useSupplierRetailer } from "../../../wrappers/SupplierRetailerContext";

const API = process.env.REACT_APP_API_URL;

// Display names for user_type values. The DB keeps the legacy 'SME' value —
// only the label changed to "Basic" (admin-v2 phase 4). 'CAM' arrived with
// TASK-user-creation-rework; 'SME' remains only for org-created viewers.
const USER_TYPE_LABELS = { admin: "Master", CAM: "CAM", org_admin: "Org Admin", supplier: "Supplier", SME: "Basic" };
const typeLabel = (t) => USER_TYPE_LABELS[t] || (t ? t.charAt(0).toUpperCase() + t.slice(1) : t);

// The type-scoped views (spec: users.master-admins.md). There is deliberately
// no "all users" view — each type gets its own columns.
const VIEWS = [
  { key: "supplier", label: "Suppliers", type: "supplier" },
  { key: "CAM", label: "CAMs", type: "CAM" },
  { key: "organisations", label: "Organisations" },
  { key: "SME", label: "Org users (Basic)", type: "SME" },
  { key: "admin", label: "Masters", type: "admin" },
];

// State, not an action: reads at a glance down a column, still toggles on click.
const StateChip = ({ on, label, onClick, title }) => (
  <Tooltip label={title} openDelay={400}>
    <Button
      size="xs"
      variant="ghost"
      onClick={onClick}
      aria-pressed={on}
      minW="38px"
      px={1}
      fontWeight="600"
      color={on ? "signal.up" : "ink.400"}
      bg={on ? "signal.upSoft" : "ink.100"}
      _hover={{ bg: on ? "signal.upSoft" : "ink.200" }}
    >
      {label}
    </Button>
  </Tooltip>
);

const fmtShort = (d) => {
  if (!d) return "";
  const date = new Date(d);
  return new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short" }).format(date);
};

const Users = () => {
  const { fetchSupplierAccess, refreshSupplierList, isMaster, selectedOrg } = useSupplierRetailer();
  const navigate = useNavigate();
  const toast = useToast();

  const [users, setUsers] = useState([]);
  const [typeOptions, setTypeOptions] = useState(["admin", "CAM", "org_admin", "supplier", "SME"]);
  const [suppliersList, setSuppliersList] = useState([]); // {suppliercode, org_code} for the org drawer
  const [pageSets, setPageSets] = useState({ pages: [], reportPages: [], camAdminPages: [], orgAdminPages: [] });
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [search, setSearch] = useState("");
  const [view, setView] = useState(() => {
    try { return localStorage.getItem("usersView") || "supplier"; } catch (e) { return "supplier"; }
  });

  // Row-action modals (one shared instance each, driven by the ⋯ menu).
  const [emailTarget, setEmailTarget] = useState(null);
  const [emailDraft, setEmailDraft] = useState({ subject: "", body: "" });
  const [typeTarget, setTypeTarget] = useState(null);
  const [typeDraft, setTypeDraft] = useState("");

  const setViewPersist = (v) => {
    setView(v);
    try { localStorage.setItem("usersView", v); } catch (e) { /* ignore */ }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await axios.post(`${API}/admin/get-users`, {});
      setUsers(response.data.users || []);
      if (Array.isArray(response.data.user_type)) setTypeOptions(response.data.user_type);
    } catch (error) {
      toast({ title: "Failed to load users", description: error?.response?.data?.message || "Technical error occurred", status: "error", duration: 4000, isClosable: true });
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchSuppliersAndPages = async () => {
    try {
      const response = await axios.post(`${API}/admin/get-suppliers-and-pages`, {});
      const d = response.data?.data || {};
      setSuppliersList(d.suppliers || []);
      setPageSets({
        pages: d.pages || [],
        reportPages: d.reportPages || [],
        camAdminPages: d.camAdminPages || [],
        orgAdminPages: d.orgAdminPages || [],
      });
    } catch (error) {
      /* popovers fall back to empty sets; the toast on get-users covers outages */
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchSuppliersAndPages();
    fetchSupplierAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeView = VIEWS.find((v) => v.key === view) || VIEWS[0];

  const rows = useMemo(() => {
    if (view === "organisations") return [];
    const q = search.trim().toLowerCase();
    return users
      .filter((row) => row.user_type === activeView.type)
      // Masters administer every org; the other views follow the org toggle
      // (rows without an org stay visible everywhere — pre-migration data).
      .filter((row) => view === "admin" || !(isMaster && selectedOrg && row.org_code && row.org_code !== selectedOrg))
      .filter((row) => !q || `${row.fname || ""} ${row.username || ""} ${row.useremail || ""} ${row.owned_suppliercode || ""}`.toLowerCase().includes(q))
      .sort((a, b) => String(a.fname || "").localeCompare(String(b.fname || ""), undefined, { sensitivity: "base" }));
  }, [users, view, search, isMaster, selectedOrg, activeView.type]);

  // ── Row actions ────────────────────────────────────────────────────────────
  const simplePost = async (path, body, okTitle) => {
    try {
      const response = await axios.post(`${API}${path}`, body);
      if (okTitle) toast({ title: okTitle, description: response.data?.description || response.data?.message, status: "success", duration: 2500, isClosable: true });
      fetchUsers();
    } catch (error) {
      toast({ title: "Update failed", description: error?.response?.data?.description || error?.response?.data?.message || "Technical error", status: "error", duration: 5000, isClosable: true });
    }
  };

  const submitField = (userid, field, value) => {
    const routes = { fname: "/admin/upd-fname", username: "/admin/upd-username", useremail: "/admin/upd-useremail", userpass: "/admin/upd-userpass" };
    simplePost(routes[field], { userid, [field]: value }, "Updated");
  };

  const toggleFlag = (row, field) => {
    const routes = { activeyn: "/admin/upd-user-activeyn", restrictedyn: "/admin/upd-user-restrictedyn", subscriptionyn: "/admin/upd-user-subscriptionyn" };
    simplePost(routes[field], { userid: row.userid, [field]: !row[field] }, undefined);
  };

  const manualPay = (userid) => simplePost("/admin/ins-manual-payment", { userid }, "Manual payment recorded");

  const resendInvite = async (userid) => {
    try {
      const response = await axios.post(`${API}/admin/resend-invite`, { userid });
      toast({
        title: "Invite re-sent",
        description: response.data.message,
        status: response.data.data?.emailed === false ? "warning" : "success",
        duration: response.data.data?.emailed === false ? 15000 : 4000,
        isClosable: true,
      });
    } catch (error) {
      toast({ title: "Failed to resend invite", description: error?.response?.data?.message, status: "error", duration: 5000, isClosable: true });
    }
  };

  const deleteUser = async (row) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ${row.fname} (${row.username})? ${row.user_type === "supplier" ? "This removes the supplier and every grant to it." : ""}`)) return;
    try {
      const response = await axios.post(`${API}/admin/del-user`, { userid: row.userid });
      toast({ title: response.data.title, description: response.data.description, status: "success", duration: 4000, isClosable: true });
    } catch (error) {
      toast({ title: "Failed to delete user", description: error?.response?.data?.description || error?.response?.data?.message, status: "error", duration: 5000, isClosable: true });
    } finally {
      fetchUsers();
      fetchSupplierAccess();
      // Deleting a supplier revokes its grants code-wide — drop it from the
      // in-memory dropdown list too, without disturbing the current selection.
      if (row.user_type === "supplier") refreshSupplierList();
    }
  };

  const sendEmail = async () => {
    try {
      await axios.post(`${API}/admin/email-user`, { userid: emailTarget.userid, email: { subject: emailDraft.subject, body: emailDraft.body } });
      toast({ title: "Email sent", status: "success", duration: 3000, isClosable: true });
      setEmailTarget(null);
      setEmailDraft({ subject: "", body: "" });
    } catch (error) {
      toast({ title: "Failed to send email", description: error?.response?.data?.message, status: "error", duration: 5000, isClosable: true });
    }
  };

  const changeType = async () => {
    await simplePost("/admin/upd-user-type", { userid: typeTarget.userid, user_type: typeDraft }, `Type changed to ${typeLabel(typeDraft)}`);
    setTypeTarget(null);
  };

  // ── Cells ──────────────────────────────────────────────────────────────────
  const InviteCell = ({ row }) => (
    <Td px={1} py={1}>
      {row.invite_pending ? (
        <Flex align="center" gap={1}>
          <Text fontSize="xs" color="orange.500" fontWeight="600" whiteSpace="nowrap">Pending</Text>
          <Button size="xs" variant="ghost" onClick={() => resendInvite(row.userid)}>Resend</Button>
        </Flex>
      ) : (
        <Text fontSize="xs" color="ink.300">—</Text>
      )}
    </Td>
  );

  const AccessCell = ({ row }) => (
    <Td px={1} py={1}>
      <Flex gap={1}>
        <StateChip on={row.activeyn} label="Signin" title="Can sign in" onClick={() => toggleFlag(row, "activeyn")} />
        <StateChip on={row.restrictedyn} label="Restr" title="Restricted (report pages hidden)" onClick={() => toggleFlag(row, "restrictedyn")} />
        <StateChip on={row.subscriptionyn} label="Sub" title="Payment subscription" onClick={() => toggleFlag(row, "subscriptionyn")} />
      </Flex>
    </Td>
  );

  const BillingCell = ({ row }) => (
    <Td px={1} py={1}>
      {row.subscriptionyn ? (
        <Flex align="center" gap={1} whiteSpace="nowrap">
          <BillingPopover
            user={row}
            onSaved={fetchUsers}
            trigger={
              <Button size="xs" variant="ghost">
                R{((Number(row.subscription_amount) || 0) / 100).toLocaleString()}{row.date_last_paid ? ` · paid ${fmtShort(row.date_last_paid)}` : ""}
              </Button>
            }
          />
          <Button size="xs" variant="secondary" onClick={() => manualPay(row.userid)}>Pay</Button>
        </Flex>
      ) : (
        <Text fontSize="xs" color="ink.300">—</Text>
      )}
    </Td>
  );

  const GrantsCell = ({ row }) => (
    <Td px={1} py={1}>
      <Flex gap={0}>
        <PagesPopover user={row} pageSets={pageSets} onSaved={fetchUsers} />
        <RetailersPopover user={row} onSaved={fetchUsers} />
        <SuppliersPopover user={row} onSaved={fetchUsers} />
      </Flex>
    </Td>
  );

  const RowMenu = ({ row }) => (
    <Td px={1} py={1}>
      <Menu isLazy>
        <MenuButton as={IconButton} icon={<FaEllipsisV />} size="xs" variant="ghost" aria-label="More actions" />
        <MenuList fontSize="sm">
          <MenuItem onClick={() => { setTypeTarget(row); setTypeDraft(row.user_type); }}>Change type…</MenuItem>
          <MenuItem onClick={() => { setEmailTarget(row); setEmailDraft({ subject: "", body: "" }); }}>Send email…</MenuItem>
          <MenuItem color="signal.down" onClick={() => deleteUser(row)}>Delete user</MenuItem>
        </MenuList>
      </Menu>
    </Td>
  );

  const headers = {
    supplier: ["Logo", "Code", "Name", "Username", "Password", "Email", "Invite", "Access", "Billing", "Grants", ""],
    CAM: ["Logo", "Name", "Username", "Password", "Email", "Invite", "Access", "Billing", "Grants", ""],
    SME: ["Name", "Org", "Username", "Password", "Email", "Invite", "Access", "Grants", ""],
    admin: ["Name", "Username", "Password", "Email"],
  };

  const renderRow = (row) => {
    const fields = {
      fname: <EditablePopover key="f" label="Name" value={row.fname} fieldName="fname" onSubmit={(f, v) => submitField(row.userid, "fname", v)} />,
      username: <EditablePopover key="u" label="Username" value={row.username} fieldName="username" onSubmit={(f, v) => submitField(row.userid, "username", v)} />,
      userpass: <EditablePopover key="p" label="Password" value={row.userpass} fieldName="userpass" onSubmit={(f, v) => submitField(row.userid, "userpass", v)} />,
      useremail: <EditablePopover key="e" label="Email" value={row.useremail} fieldName="useremail" maxW="200px" onSubmit={(f, v) => submitField(row.userid, "useremail", v)} />,
    };
    if (view === "supplier") {
      return (
        <Tr key={row.userid}>
          <Td px={1} py={1}><LogoCell storedCode={row.owned_suppliercode || row.fname} label={row.fname} /></Td>
          <Td px={1} py={1} fontWeight="600" whiteSpace="nowrap">{row.owned_suppliercode || <Text as="span" fontSize="xs" color="signal.down">no supplier row</Text>}</Td>
          {fields.fname}
          {fields.username}
          {fields.userpass}
          {fields.useremail}
          <InviteCell row={row} />
          <AccessCell row={row} />
          <BillingCell row={row} />
          <GrantsCell row={row} />
          <RowMenu row={row} />
        </Tr>
      );
    }
    if (view === "CAM") {
      return (
        <Tr key={row.userid}>
          <Td px={1} py={1}><LogoCell storedCode={`CAM_${row.userid}`} label={row.fname} /></Td>
          {fields.fname}
          {fields.username}
          {fields.userpass}
          {fields.useremail}
          <InviteCell row={row} />
          <AccessCell row={row} />
          <BillingCell row={row} />
          <GrantsCell row={row} />
          <RowMenu row={row} />
        </Tr>
      );
    }
    if (view === "SME") {
      return (
        <Tr key={row.userid}>
          {fields.fname}
          <Td px={1} py={1}>{row.org_code || "—"}</Td>
          {fields.username}
          {fields.userpass}
          {fields.useremail}
          <InviteCell row={row} />
          <AccessCell row={row} />
          <GrantsCell row={row} />
          <RowMenu row={row} />
        </Tr>
      );
    }
    // masters: self-service fields only — no toggles, no delete.
    return (
      <Tr key={row.userid}>
        {fields.fname}
        {fields.username}
        {fields.userpass}
        {fields.useremail}
      </Tr>
    );
  };

  const addActions = () => {
    if (view === "supplier") return <Button leftIcon={<FaPlus />} onClick={() => navigate("/addUser?type=supplier")}>Add Supplier</Button>;
    if (view === "CAM") return <Button leftIcon={<FaPlus />} onClick={() => navigate("/addUser?type=CAM")}>Add CAM</Button>;
    if (view === "organisations") {
      return (
        <>
          <Button variant="subtle" leftIcon={<FaPlus />} onClick={() => navigate("/addUser?type=org_admin")}>Add Org Admin</Button>
          <CreateOrgPopover onCreated={fetchUsers} />
        </>
      );
    }
    if (view === "SME") return <Text fontSize="xs" color="ink.500">Org users are created by their org admins (invites)</Text>;
    return null;
  };

  return (
    <Box>
      <PageToolbar
        filters={
          <>
            <Select aria-label="User type view" value={view} onChange={(e) => setViewPersist(e.target.value)} width="190px">
              {VIEWS.map((v) => (
                <option key={v.key} value={v.key}>{v.label}</option>
              ))}
            </Select>
            <Input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} width="220px" />
            {view !== "organisations" && (
              <Text fontSize="sm" color="ink.500" whiteSpace="nowrap">
                {rows.length} {rows.length === 1 ? "user" : "users"}
              </Text>
            )}
          </>
        }
        actions={addActions()}
      />

      {view === "organisations" ? (
        <OrganisationsView allUsers={users} allSuppliers={suppliersList} search={search} />
      ) : (
        <Panel padded={false} maxH="calc(100vh - 240px)" overflow="auto">
          {loadingUsers ? (
            <TableSkeleton rows={12} minH="420px" />
          ) : (
            <Table variant="striped" size="sm" colorScheme="custom" textStyle="smallText">
              <Thead position="sticky" top={0} zIndex={1} bg="white">
                <Tr>
                  {headers[view].map((h, i) => (
                    <Th key={`${h}-${i}`} px={1} py={1} bg="gray.50">{h}</Th>
                  ))}
                </Tr>
              </Thead>
              <Tbody>{rows.map(renderRow)}</Tbody>
            </Table>
          )}
        </Panel>
      )}

      {/* Send email modal (shared instance, driven from the ⋯ menu) */}
      <Modal isOpen={!!emailTarget} onClose={() => setEmailTarget(null)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Email {emailTarget?.fname}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={2}>
              <FormLabel fontSize="sm">Subject</FormLabel>
              <Input size="sm" value={emailDraft.subject} onChange={(e) => setEmailDraft({ ...emailDraft, subject: e.target.value })} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Body</FormLabel>
              <Textarea size="sm" rows={6} value={emailDraft.body} onChange={(e) => setEmailDraft({ ...emailDraft, body: e.target.value })} />
            </FormControl>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button size="sm" variant="ghost" onClick={() => setEmailTarget(null)}>Cancel</Button>
            <Button size="sm" colorScheme="teal" onClick={sendEmail} isDisabled={!emailDraft.subject && !emailDraft.body}>Send</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Change type modal */}
      <Modal isOpen={!!typeTarget} onClose={() => setTypeTarget(null)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Change type — {typeTarget?.fname}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Select value={typeDraft} onChange={(e) => setTypeDraft(e.target.value)}>
              {typeOptions.map((t) => (
                <option key={t} value={t}>{typeLabel(t)}</option>
              ))}
            </Select>
            <Text fontSize="xs" color="ink.500" mt={2}>
              The user moves to the matching view. They must sign in again for the change to fully apply.
            </Text>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button size="sm" variant="ghost" onClick={() => setTypeTarget(null)}>Cancel</Button>
            <Button size="sm" colorScheme="teal" onClick={changeType} isDisabled={!typeDraft || typeDraft === typeTarget?.user_type}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Users;
